import { sendTestPush } from 'api/MobileAppsAPI';
import { InputField, TextAreaField } from 'lib/iterable-hook-form';
import { fetchIntegrations } from 'modules/mobileApps/actions';
import {
  getEnvironmentLabelByPlatform,
  getFormErrorMessages,
} from 'modules/mobileApps/components/utils';
import { TOAST_ACTIONS } from 'modules/mobileApps/constants';
import { useIntegrationMetadataContext } from 'modules/mobileApps/context/integrationMetadata';
import { ModalType, useModalContext } from 'modules/mobileApps/context/modal';
import { TestPushFormField, useTestPushFormContext } from 'modules/mobileApps/context/testPushForm';
import { IntegrationPlatform } from 'modules/mobileApps/types';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { SuccessBanner } from 'ui/banner';
import { BUTTON_SIZES, Button, SecondaryButton } from 'ui/button';
import { FormField } from 'ui/formField';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { spacing } from 'ui/theme';
import { getIsModalOpen } from './utils';

const StyledBanner = styled(SuccessBanner)`
  margin-bottom: ${spacing.plusHalf};
`;

export const TEST_PUSH_MODAL_ID = 'test-push-modal';

const HEXADECIMAL_REGEX = /^[0-9a-fA-F]+$/;

type Props = { fileName?: string; isTestingLater?: boolean };

export const TestPushModal = ({ fileName, isTestingLater = false }: Props) => {
  const dispatch = useDispatch();
  const { closeModal, openedModals } = useModalContext();
  const { selectedIntegration } = useIntegrationMetadataContext();
  const {
    formState: { errors },
    handleSubmit,
    setError,
    watch,
  } = useTestPushFormContext();

  const deviceToken = watch(TestPushFormField.deviceToken);
  const testMessage = watch(TestPushFormField.testMessage);

  const errorMessages = getFormErrorMessages(errors);

  const handleClose = () => {
    closeModal(ModalType.TestPushModal);
  };

  const handleSendTest = async () => {
    if (!selectedIntegration) return;

    if (selectedIntegration.platform !== IntegrationPlatform.GCM) {
      const isHexFormat = HEXADECIMAL_REGEX.test(deviceToken);

      if (!isHexFormat) {
        setError(TestPushFormField.deviceToken, {
          message: 'Invalid hex format',
          type: 'validate',
        });
        return;
      }
    }

    try {
      await sendTestPush({
        deviceToken,
        message: testMessage,
        pushConfig: selectedIntegration,
      });
      dispatch(addPositiveToast(`Test to ${deviceToken} was successfully sent.`));
      dispatch(fetchIntegrations());
      handleClose();
    } catch (error: any) {
      dispatch(
        addNegativeToastWithErrorId(
          `Unable to send test to ${deviceToken}. Please try again.`,
          TOAST_ACTIONS.testPush,
          error,
        ),
      );
    }
  };

  return (
    <Modal
      data-test={TEST_PUSH_MODAL_ID}
      id={TEST_PUSH_MODAL_ID}
      isModalOpen={getIsModalOpen(openedModals, ModalType.TestPushModal)}
      onClose={handleClose}
      withPortal
    >
      <ModalHeader data-test={`${TEST_PUSH_MODAL_ID}-header`}>
        Test {getEnvironmentLabelByPlatform(selectedIntegration?.platform)} integration
      </ModalHeader>
      <ModalBody>
        {!isTestingLater && (
          <StyledBanner>
            Your <strong>{fileName}</strong> file was saved successfully! To ensure seamless
            integration, we recommend sending a test push notification.
          </StyledBanner>
        )}
        <FormField
          errorText={errorMessages.deviceToken}
          hasError={!!errorMessages.deviceToken}
          helpText={
            !errorMessages.deviceToken &&
            selectedIntegration?.platform !== IntegrationPlatform.GCM &&
            'Enter device token in hex format'
          }
          labelText="Device token"
        >
          <InputField
            data-test={`${TEST_PUSH_MODAL_ID}-device-token-input`}
            hasIcon={false}
            name={TestPushFormField.deviceToken}
          />
        </FormField>
        <FormField
          errorText={errorMessages.testMessage}
          hasError={!!errorMessages.testMessage}
          labelText="Message"
        >
          <TextAreaField
            data-test={`${TEST_PUSH_MODAL_ID}-message-input`}
            autoGrowMinHeight={spacing.x5}
            hasIcon={false}
            name={TestPushFormField.testMessage}
          />
        </FormField>
      </ModalBody>
      <ModalFooter hasCancel={false}>
        {!isTestingLater && (
          <SecondaryButton onClick={handleClose} size={BUTTON_SIZES.LARGE}>
            Send test later
          </SecondaryButton>
        )}
        <Button
          data-test={`${TEST_PUSH_MODAL_ID}-send-test-button`}
          onClick={handleSubmit(handleSendTest)}
          size={BUTTON_SIZES.LARGE}
        >
          Send test
        </Button>
      </ModalFooter>
    </Modal>
  );
};
